import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useSiteData } from '@/context/SiteDataContext';

// Simple field set for any page
const heroFields = [
  { key: 'hero_overline', label: 'Hero overline (small text)', type: 'text' },
  { key: 'hero_heading', label: 'Hero heading', type: 'text' },
  { key: 'hero_text', label: 'Hero paragraph', type: 'textarea' },
];

const PAGES = [
  { slug: 'home', label: 'Home Page', fields: [
    { key: 'hero_overline', label: 'Hero overline', type: 'text' },
    { key: 'welcome_overline', label: 'Welcome overline', type: 'text' },
    { key: 'welcome_heading', label: 'Welcome heading', type: 'text' },
    { key: 'welcome_p1', label: 'Welcome paragraph 1', type: 'textarea' },
    { key: 'welcome_p2', label: 'Welcome paragraph 2', type: 'textarea' },
    { key: 'pillars_overline', label: 'Pillars overline', type: 'text' },
    { key: 'pillars_heading', label: 'Pillars heading', type: 'text' },
    { key: 'pillar1_title', label: 'Pillar 1 title', type: 'text' },
    { key: 'pillar1_text', label: 'Pillar 1 body', type: 'textarea' },
    { key: 'pillar2_title', label: 'Pillar 2 title', type: 'text' },
    { key: 'pillar2_text', label: 'Pillar 2 body', type: 'textarea' },
    { key: 'pillar3_title', label: 'Pillar 3 title', type: 'text' },
    { key: 'pillar3_text', label: 'Pillar 3 body', type: 'textarea' },
    { key: 'pillar4_title', label: 'Pillar 4 title', type: 'text' },
    { key: 'pillar4_text', label: 'Pillar 4 body', type: 'textarea' },
    { key: 'events_overline', label: 'Events overline', type: 'text' },
    { key: 'events_heading', label: 'Events heading', type: 'text' },
    { key: 'depts_overline', label: 'Departments overline', type: 'text' },
    { key: 'depts_heading', label: 'Departments heading', type: 'text' },
    { key: 'timeline_overline', label: 'Timeline overline', type: 'text' },
    { key: 'timeline_heading', label: 'Timeline heading', type: 'text' },
    { key: 'news_overline', label: 'News overline', type: 'text' },
    { key: 'news_heading', label: 'News heading', type: 'text' },
    { key: 'cta_overline', label: 'Giving CTA overline', type: 'text' },
    { key: 'cta_heading', label: 'Giving CTA heading', type: 'text' },
    { key: 'cta_text', label: 'Giving CTA body', type: 'textarea' },
  ]},
  { slug: 'about', label: 'About Page', fields: [
    ...heroFields,
    { key: 'history_overline', label: 'History overline', type: 'text' },
    { key: 'history_heading', label: 'History heading', type: 'text' },
    { key: 'history_text', label: 'History paragraph', type: 'textarea', rows: 6 },
    { key: 'mvv_mission_title', label: 'Mission — Title', type: 'text' },
    { key: 'mvv_mission', label: 'Mission — Text', type: 'textarea', rows: 3 },
    { key: 'mvv_vision_title', label: 'Vision — Title', type: 'text' },
    { key: 'mvv_vision', label: 'Vision — Text', type: 'textarea', rows: 3 },
    { key: 'mvv_values_title', label: 'Core Values — Title', type: 'text' },
    { key: 'mvv_values', label: 'Core Values — Text', type: 'textarea', rows: 3 },
    { key: 'faith_overline', label: 'Statement of Faith — Overline', type: 'text' },
    { key: 'faith_heading', label: 'Statement of Faith — Heading', type: 'text' },
    { key: 'timeline_overline', label: 'Timeline overline', type: 'text' },
    { key: 'timeline_heading', label: 'Timeline heading', type: 'text' },
  ]},
  { slug: 'leadership', label: 'Leadership Page', fields: [
    ...heroFields,
    { key: 'section1_title', label: 'Section 1 title (Episcopal)', type: 'text' },
    { key: 'section1_subtitle', label: 'Section 1 subtitle', type: 'text' },
    { key: 'section2_title', label: 'Section 2 title (Executive Admin)', type: 'text' },
    { key: 'section2_subtitle', label: 'Section 2 subtitle', type: 'text' },
    { key: 'section3_title', label: 'Section 3 title (District)', type: 'text' },
    { key: 'section3_subtitle', label: 'Section 3 subtitle', type: 'text' },
    { key: 'section4_title', label: 'Section 4 title (Pastors & Elders)', type: 'text' },
    { key: 'section4_subtitle', label: 'Section 4 subtitle', type: 'text' },
    { key: 'section5_title', label: 'Section 5 title (Ordination)', type: 'text' },
    { key: 'section5_subtitle', label: 'Section 5 subtitle', type: 'text' },
    { key: 'section6_title', label: 'Section 6 title (Adjutancy)', type: 'text' },
    { key: 'section6_subtitle', label: 'Section 6 subtitle', type: 'text' },
    { key: 'section7_title', label: 'Section 7 title (Dept Presidents)', type: 'text' },
    { key: 'section7_subtitle', label: 'Section 7 subtitle', type: 'text' },
  ]},
  { slug: 'departments', label: 'Departments Page', fields: heroFields },
  { slug: 'districts', label: 'Districts Page', fields: heroFields },
  { slug: 'events', label: 'Events Page', fields: heroFields },
  { slug: 'calendar', label: 'Calendar Page', fields: heroFields },
  { slug: 'news', label: 'News Page', fields: heroFields },
  { slug: 'media', label: 'Media Page', fields: [
    ...heroFields,
    { key: 'live_overline', label: 'Live section overline', type: 'text' },
    { key: 'live_heading', label: 'Live section heading', type: 'text' },
    { key: 'gallery_overline', label: 'Gallery section overline', type: 'text' },
    { key: 'gallery_heading', label: 'Gallery section heading', type: 'text' },
  ]},
  { slug: 'locator', label: 'Churches / Locator', fields: heroFields },
  { slug: 'contact', label: 'Contact Page', fields: [
    ...heroFields,
    { key: 'form_heading', label: 'Contact form heading', type: 'text' },
  ]},
];

export default function PageContentEditor() {
  const [slug, setSlug] = useState('home');
  const [content, setContent] = useState({});
  const [busy, setBusy] = useState(false);
  const { loadPageContent } = useSiteData();

  useEffect(() => {
    api.get(`/site/page-content/${slug}`).then(r => setContent(r.data || {})).catch(() => setContent({}));
  }, [slug]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/site/page-content/${slug}`, content);
      toast.success('Page content saved — changes are live');
      loadPageContent?.(slug);
    }
    catch { toast.error('Save failed'); } finally { setBusy(false); }
  };

  const page = PAGES.find(p => p.slug === slug);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <h3 className="font-serif text-xl text-royal font-bold">Page Content</h3>
        <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="page-content-select" className="px-3 py-2 border rounded">
          {PAGES.map(p => <option key={p.slug} value={p.slug}>{p.label}</option>)}
        </select>
        <button onClick={save} disabled={busy} data-testid="page-content-save-btn" className="btn-royal ml-auto">{busy ? 'Saving...' : 'Save'}</button>
      </div>
      <div className="bg-white border rounded-lg p-6 space-y-4 max-w-3xl">
        {page.fields.map(f => (
          <div key={f.key}>
            <div className="text-sm font-semibold text-royal mb-1">{f.label}</div>
            {f.type === 'textarea'
              ? <textarea rows={f.rows || 3} value={content[f.key] || ''} onChange={(e) => setContent({ ...content, [f.key]: e.target.value })} className="w-full px-3 py-2 border rounded" />
              : <input value={content[f.key] || ''} onChange={(e) => setContent({ ...content, [f.key]: e.target.value })} className="w-full px-3 py-2 border rounded" />}
          </div>
        ))}
      </div>
    </div>
  );
}
